export namespace AgentNs {

  // ─── Routes ───────────────────────────────────────────────────

  export enum AgentRouteEnums {
    AGENT_TEST = 'AgentTest',
    AGENT_TEST_HARNESS = 'AgentTestHarness',
    LLM_AUDIT = 'AgentLlmAudit'
  }

  // ─── Enums ────────────────────────────────────────────────────

  export type GenerationType = 'modules' | 'modules_features' | 'full_structure';
  export type AgentMode = 'generate' | 'refine' | 'resolve' | 'generate_questions' | 'generate_enrich_questions';
  export type ChatAction =
    | 'answer'
    | 'analyze_document'
    | 'analyze_input'
    | 'add_to_manual_input'
    | 'modify_manual_input'
    | 'clarify'
    | 'route_to_agent';

  // ─── Project State ────────────────────────────────────────────

  /**
   * Legacy `project_context` topic object — `{topic, topic_id, weight}`. Persisted state written
   * before the prompt rule switched `covered` / `gaps` to flat string lists can still carry it, so
   * the tolerant {@link ProjectContext} accepts both shapes. A LIVE envelope never does: the
   * backend contract (`AgentProjectContext`) declares string lists and rejects anything else.
   */
  export interface ContextTopic {
    topic: string;
    topic_id: string;
    weight: number;
  }

  /**
   * Live `project_context` — the exact mirror of the backend `AgentProjectContext` record
   * (F5-T5.2). Every key is optional because the modes differ: BUILDER `resolve` emits only
   * `confidence`, ANALYZE emits `confidence` + `platform_type` + `market_type`, BUILDER generate
   * emits the list slots. `covered`, `covered_concepts`, `gaps` and `entities` are FLAT STRING
   * LISTS — the object form is legacy persisted data only.
   *
   * <p>Hand-synchronized with the backend DTO, field for field; there is no codegen pipeline here.
   */
  export interface LiveProjectContext {
    summary?: string | null;
    confidence?: number | null;
    platform_type?: string | null;
    market_type?: string | null;
    entities?: string[] | null;
    covered?: string[] | null;
    covered_concepts?: string[] | null;
    gaps?: string[] | null;
    built_at?: string | null;
  }

  /**
   * Tolerant `project_context` as it lives in {@link ProjectState}: the backend merges each live
   * update over whatever the project already had, so this value is the union of the live contract
   * and older persisted shapes. Read it defensively — use {@link contextTopicLabels} instead of
   * indexing `covered` / `gaps` directly.
   */
  export interface ProjectContext {
    summary?: string | null;
    confidence?: number | null;
    platform_type?: string | null;
    market_type?: string | null;
    entities?: string[] | null;
    covered?: (string | ContextTopic)[] | null;
    covered_concepts?: string[] | null;
    gaps?: (string | ContextTopic)[] | null;
    built_at?: string | null;
  }

  /**
   * Renders a tolerant `covered` / `gaps` list as display labels, accepting both the live string
   * form and the legacy `{topic}` object form.
   */
  export function contextTopicLabels(
    list: (string | ContextTopic)[] | null | undefined
  ): string[] {
    if (!list) return [];
    return list
      .map(entry => (typeof entry === 'string' ? entry : entry?.topic))
      .filter((label): label is string => typeof label === 'string' && label.trim().length > 0);
  }

  /**
   * Frontend representation of the project state. Mirrors backend's
   * {@code ProjectStateDto} — but only the fields the frontend actively needs.
   * <p>
   * The canonical input list is {@link inputs}; each entry's {@code source}
   * field (`document` | `text` | `additional`) carries what was previously
   * three separate slots. Use the helper filters in
   * {@code agent-chat.store#getDocumentInputs / getFreeInputs / getManualInputs}
   * when a per-source view is needed.
   */
  export interface ProjectState {
    inputs: Input[];
    project_summary: string | null;
    project_context: ProjectContext | null;
    existing_structure: Structure | null;
    /**
     * BUILDER `resolve` mode input — user answers from the INTERVIEW
     * `solve_open_points` flow. One entry per resolved open_point. Shape is
     * intentionally loose because the LLM prompt only needs the open_point
     * text + the answer; additional context (element_id, element_name,
     * question_text) helps the LLM locate the right module.
     */
    resolve_questions?: ResolveQuestionAnswer[] | null;
    /**
     * Historical record of confirmed decisions — backend ProjectStateDto
     * `resolved_points` field. Populated by AnalyzeAgent (when LLM emits)
     * and used as context by BUILDER/INTERVIEW agents. Frontend reads it
     * to display "decisions made so far" (audit/history view).
     * <p>
     * Shape is loose (Map<String,Object>) matching backend
     * `List<Map<String, Object>>`. Typical fields: open_point, answer,
     * element_id, element_name, confirmed_at.
     */
    resolved_points?: Array<Record<string, unknown>> | null;
  }

  /**
   * One resolved open-point handed to BUILDER `resolve` mode. The frontend
   * builds these from the QA widget's qaQuestions + qaBatchAnswers when the
   * user finishes the Resolve flow.
   */
  export interface ResolveQuestionAnswer {
    open_point: string;
    element_id?: string | null;
    element_name?: string | null;
    question: string;
    answer: string;
    skipped?: boolean;
    /** Echoed verbatim from {@link Question.open_point_key} when the source question carried one. */
    open_point_key?: string | null;
  }

  // ─── Inputs (source-of-truth for all derived data) ────────────
  //
  // Each `inputs[]` entry represents a single source (uploaded document or
  // user-typed text) and OWNS its derived data nested inside:
  // `captured_topics`, `decisions`, `open_points`, `project_notes`,
  // `entities`. The frontend aggregates across inputs to produce the
  // global Context Card view.

  /**
   * Backend stempelt drei Werte (siehe AnalyzeAgent.sourceForAction):
   * - `document`  : analyze_document → Datei-Upload.
   * - `text`      : analyze_input → initiale Freitext-Projektbeschreibung.
   * - `additional`: add_input → spätere Freitext-Ergänzung NACH der initialen Analyse.
   *
   * Frontend mappt `document` + `text` auf den "Hauptkontext"-Bucket,
   * `additional` auf den "Additions"-Bucket im Context Card.
   */
  export type InputSource = 'document' | 'text' | 'additional';

  /** UI-Bucket für die Context-Card-Tab-Logik. */
  export type ContextBucket = 'main' | 'addition';

  export type DecisionEntry = {
    title: string;
    quote: string | null;
    stakeholder: string | null;
    rationale: string;
  };

  export type ProjectNoteCategory =
    | 'launch'
    | 'migration'
    | 'meeting'
    | 'follow_up'
    | 'documentation'
    | 'training'
    | 'other';

  export type ProjectNoteEntry = {
    title: string;
    summary: string;
    category: ProjectNoteCategory;
  };

  export type EntityType =
    | 'person'
    | 'role'
    | 'system'
    | 'tool'
    | 'product'
    | 'org'
    | 'other';

  export type EntityEntry = {
    name: string;
    type: EntityType;
    summary: string;
  };

  export interface Input {
    id: string;
    source: InputSource;
    name: string;
    title: string;
    summary: string;
    type: string;
    source_date: string;
    added_at?: string;
    modified_at?: string;
    captured_topics?: { title: string; summary: string }[];
    decisions?:       DecisionEntry[];
    open_points?:     { title: string; summary: string }[];
    project_notes?:   ProjectNoteEntry[];
    entities?:        EntityEntry[];
  }

  // ─── ANALYZE modify/remove targets ───────────────────────────

  export type AnalyzeTarget =
    | 'input'
    | 'captured_topic'
    | 'decision'
    | 'open_point'
    | 'project_note'
    | 'entity';

  // ─── Chat Request / Response ──────────────────────────────────

  export type AiProvider = 'openai' | 'claude';

  /**
   * Frontend → backend chat request.
   * <p>
   * JSON body for the standard chat path. When the user attaches new files
   * the request is wrapped in multipart/form-data with this object serialised
   * as the `payload` part — see {@link AgentApi.chat}.
   * <p>
   * `projectState` content stays snake_case (LLM prompt contract).
   */
  export interface AgentChatRequest {
    userInput: string;
    /**
     * Pre-uploaded documents (already in project_document) to include in this
     * turn — backend resolves the markdown content from each key.
     */
    documentKeys?: string[] | null;
    /** AI sağlayıcı: "openai" | "claude" — belirtilmezse backend "openai" kullanır */
    aiProvider?: AiProvider;
    projectId?: string | null;
    workspaceId?: string | null;
    /** Direct-route hint that bypasses INTENT_ROUTER. Format: "AGENT:variant_key" (e.g. "BUILDER:add_features"). */
    actionHintOverride?: string | null;
    /**
     * Target elements for `add_features` / `add_subfeatures` modes.
     * Orchestrator pre-filters the tree and passes only the target modules (for add_features)
     * or target features + parent_module (for add_subfeatures). No sibling context is sent.
     * Omitted for all other modes.
     */
    targets?: unknown[] | null;
    /**
     * INTERVIEW iki-fazlı akış göstergesi. Yalnız INTERVIEW intent'i için anlamlı:
     * - `null`/undefined veya `'questions'`: Faz 1 (default) — sorular üretilir.
     * - `'answers'`: Faz 2 — kullanıcı cevap verdi. {@link userInput} içinde
     *   frontend'in formatladığı `Q: ... \n A: ...` blokları gelir. Backend
     *   son INTERVIEW Faz 1 mesajından `pending_questions[]`'i history'den çıkartır.
     * - `'discard'`: Kullanıcı interview'i kapattı. Backend LLM çağrısı yapmadan
     *   `status: 'discarded'` envelope döner (cost + latency tasarrufu).
     */
    phase?: 'questions' | 'answers' | 'discard' | null;
    /** INTERVIEW enrich_context derinlik parametresi: quick (3-4 soru), medium (6-8), deep (10-12). */
    depth?: 'quick' | 'medium' | 'deep' | null;
    /**
     * Additive typed open-point answers (F2-T2.3). Populated only for questions that carried a
     * backend-stamped {@link Question.open_point_key} — the orchestrator applies these as
     * canonical `ANSWERED` transitions independently of (not instead of) the existing free-text
     * `resolve_questions` flow. Omitted entirely for flag-off turns or unmatched questions.
     */
    openPointAnswers?: OpenPointAnswerRequest[] | null;
    /** User answers for draft points, including explicit skips; no project state is submitted. */
    acceptOpenPointProposal?: {messageId: number; proposalIndex: number};
    /** Accepts a server-stored summary update proposal; no replacement text is submitted. */
    acceptSummaryProposal?: {messageId: number; proposalIndex: number};
    /**
     * The feature, module or page open in the feature-side chat. A request that names no element
     * is about this one; omitted in the general chat.
     */
    selectedElementId?: string | null;
    /** Accepts a server-stored content update proposal for an inserted element. */
    acceptContentProposal?: {
      proposalKey?: string | null;
      action?: 'accept' | 'reject';
      siblingProposalKey?: string;
      /** T6.3: several creation proposals accepted in one turn ("Add all"). */
      proposalKeys?: string[];
    };
    resolveAnswers?: Array<{
      elementId: string | null;
      openPoint: string;
      answer: string;
      skipped: boolean;
    }>;
  }

  /** Single entry of {@link AgentChatRequest.openPointAnswers}. */
  export interface OpenPointAnswerRequest {
    openPointKey: string;
    answer: string;
    skipped: boolean;
  }

  // Chat response may be a plain string OR an object with optional `text` +
  // `hint` — hint renders below the bubble as a secondary greyed-out line.
  export type ChatResponseText = string | { text?: string; hint?: string } | null;

  /**
   * Backend'in çalıştırdığı agent. Orkestratör her yanıtta hangi agent'ın
   * çıktı ürettiğini bu alana stamp'ler.
   */
  export type AgentName =
    | 'INTENT_ROUTER'
    | 'ANSWER'
    | 'CLARIFY'
    | 'QUERY'
    | 'MUTATION'
    | 'ANALYZE'
    | 'BUILDER'
    | 'INTERVIEW';

  /**
   * Live `chat_response` shape (F5-T5.2). The backend contract fixes exactly two keys and rejects a
   * plain-string `chat_response` from a provider, so live envelopes always carry this object.
   * {@link ChatResponseText} stays the tolerant shape for persisted history.
   */
  export interface LiveChatResponse {
    text: string;
    hint: string | null;
  }

  /**
   * Transport fields every live agent envelope carries, in every agent and every variant.
   * Synchronized by hand with the backend `AgentOutput` interface (F5-T5.2) — there is no
   * OpenAPI/codegen pipeline in this project, so the two contracts are kept in step by review.
   */
  export interface AgentEnvelopeBase {
    status: 'ok' | 'discarded' | 'needs_input';
    chat_response: LiveChatResponse;
    /** Always a tag array. The typed next-action command model is T5.7 scope. */
    next_actions: string[];
  }

  // ─── Per agent/variant payloads — mirror of the backend output DTOs ──────────
  //
  // One interface per registered backend contract key. Every field is optional exactly where the
  // backend DTO allows it to be absent, and NO interface has an index signature: a key the backend
  // contract does not declare cannot appear in a live envelope, because the backend rejects the
  // whole response before it is serialized.

  /** Backend `AnswerOutput`. */
  export type AnswerPayload = AgentEnvelopeBase;

  /** Backend `ClarifyOutput`. */
  export type ClarifyPayload = AgentEnvelopeBase;

  /** Backend `QueryOutput`. `data` / `result` are the contract's opaque value slots. */
  export interface QueryPayload extends AgentEnvelopeBase {
    data?: Record<string, unknown> | null;
    result?: Record<string, unknown> | null;
  }

  /** Backend `AnalyzeInputsOutput` — `analyze_document`, `analyze_input`, `add_input`. */
  export interface AnalyzeInputsPayload extends AgentEnvelopeBase {
    target?: AnalyzeTarget;
    project_summary?: string | null;
    project_context?: LiveProjectContext | null;
    inputs?: Input[];
  }

  /** Backend `AnalyzeMutationOutput` — `modify_input`, `remove_input`. */
  export interface AnalyzeMutationPayload extends AgentEnvelopeBase {
    target?: AnalyzeTarget;
    target_title?: string;
    target_input_id?: string;
    new_title?: string;
    new_summary?: string;
    new_category?: ProjectNoteCategory;
    new_rationale?: string;
    new_stakeholder?: string | null;
    new_type?: EntityType;
    project_summary?: string | null;
    project_context?: LiveProjectContext | null;
  }

  /** Backend `AnalyzeExtractDetailsOutput` — the silent async side call. */
  export interface AnalyzeExtractDetailsPayload extends AgentEnvelopeBase {
    inputs?: Input[];
  }

  /** Backend `InterviewQuestionsOutput`. Exactly one slot is populated, chosen by the mode. */
  export interface InterviewQuestionsPayload extends AgentEnvelopeBase {
    questions?: Question[];        // enrich_context, phase=questions
    open_questions?: Question[];   // solve_open_points
  }

  /** Backend `InterviewResolutionOutput` — enrich_context, phase=answers. */
  export interface InterviewResolutionPayload extends AgentEnvelopeBase {
    enrich_inputs?: EnrichInput[];
    project_context?: LiveProjectContext | null;
  }

  /** Backend `BuilderStructureOutput` — every mode except `diff`. */
  export interface BuilderStructurePayload extends AgentEnvelopeBase {
    open_point_update_proposals?: OpenPointUpdateProposal[];
    summary_update_proposals?: SummaryUpdateProposal[];
    proposal_source_message_id?: number;
    pages?: Page[];
    modules?: Module[];
    project_context?: LiveProjectContext | null;
  }

  /** Backend `BuilderDiffOutput` — `diff` mode only. */
  /**
   * BUILDER `diff` — a DELTA, never a snapshot. `new_modules[]` carries full module objects,
   * `updated_modules[]` only what changed, with open points expressed through
   * `added_open_points[]` / `resolved_open_points[]`. The client merges it into the committed
   * structure via `applyDiffPayload()`; project state stays the backend's full snapshot.
   */
  export interface BuilderDiffPayload extends AgentEnvelopeBase {
    new_modules?: NewModuleDelta[];
    updated_modules?: UpdatedModuleDelta[];
  }

  /** Every live payload the backend can return today. */
  export type TypedAgentPayload =
    | AnswerPayload
    | ClarifyPayload
    | QueryPayload
    | AnalyzeInputsPayload
    | AnalyzeMutationPayload
    | AnalyzeExtractDetailsPayload
    | InterviewQuestionsPayload
    | InterviewResolutionPayload
    | BuilderStructurePayload
    | BuilderDiffPayload;

  /**
   * Agent envelope — the union of every live payload, flattened.
   *
   * <p>Historically this was one loose interface with `[key: string]: any`, because the backend
   * forwarded whatever the LLM produced. Since F5-T5.2 the backend parses provider output into a
   * typed DTO, validates it against a closed JSON schema (`additionalProperties: false`) plus
   * semantic rules, and rejects anything that does not fit — so an undeclared key can no longer
   * reach the frontend and the index signature is gone with it.
   *
   * <p>The flattened form (all slots optional on one type) is kept deliberately instead of a
   * discriminated union at this call site: the store branches on the top-level `agent` /
   * `variantKey` transport fields, not on the payload shape. {@link TypedAgentPayload} and the
   * per-contract interfaces above are the union that documents which slot belongs to which
   * agent/variant, and the narrowing helpers below are how a caller moves from one to the other.
   *
   * <p>Persisted history is a different contract: see {@link LegacyAgentEnvelope}.
   */
  export interface AgentEnvelope extends Partial<AgentEnvelopeBase> {
    // ANALYZE slots
    project_summary?: string | null;
    /** Live contract shape. Persisted history uses the tolerant {@link LegacyAgentEnvelope}. */
    project_context?: LiveProjectContext | null;
    /**
     * Source-of-truth array. Every derived bucket (captured_topics, decisions,
     * open_points, project_notes, entities) is NESTED inside each entry —
     * there are no top-level lists. The store aggregates by walking
     * `inputs[*].<bucket>`.
     */
    inputs?: Input[];
    // ANALYZE modify/remove envelope slots — backend applies the change in-place.
    target?:           AnalyzeTarget;
    target_title?:     string;
    target_input_id?:  string;
    new_title?:        string;
    new_summary?:      string;
    new_category?:     ProjectNoteCategory;
    new_rationale?:    string;
    new_stakeholder?:  string | null;
    new_type?:         EntityType;

    // BUILDER slots
    open_point_update_proposals?: OpenPointUpdateProposal[];
    summary_update_proposals?: SummaryUpdateProposal[];
    proposal_source_message_id?: number;
    pages?: Page[];
    modules?: Module[];
    /** Delta slots of BUILDER `diff` — see {@link BuilderDiffPayload}, never a full snapshot. */
    new_modules?: NewModuleDelta[];
    updated_modules?: UpdatedModuleDelta[];

    // INTERVIEW slots
    questions?: Question[];        // enrich_context phase=questions
    open_questions?: Question[];   // solve_open_points

    enrich_inputs?: EnrichInput[]; // enrich_context phase=answers

    /**
     * QUERY's single contract-defined opaque value slot. The undocumented `result` slot was
     * removed from the live contract in F5-T5.2 — a live response carrying it is rejected by the
     * backend; persisted history that still has it is read through {@link LegacyAgentEnvelope}.
     */
    data?: unknown;

    /**
     * Legacy structure slots. No current BUILDER prompt emits them, so they are `undefined` on any
     * live envelope; they remain declared because persisted history from before the typed contract
     * can still carry them and {@code buildStructureFromEnvelope} reads history too.
     */
    sections?: Section[];
    generation_type?: GenerationType;
    confidence?: number;
    assumption_label?: string;
    diff?: StructureDiff;

    /** INTENT_ROUTER debug slots — the router envelope never reaches the frontend. */
    intent?: string;
    variant_key?: string | null;
    reason?: string;

    /**
     * ANALYZE `analyze_document` held turn: the backend uploaded the file but did
     * NOT add it to the project (unrelated / conflicting / uncertain / failed
     * assessment). Present only when `status === 'discarded'`. The user resolves
     * it via {@link AgentApi.decideDocument}. Also persisted in history.
     */
    document_decision?: DocumentDecisionBlock | null;

    /** MUTATION: description changes prepared for review; nothing is written until accepted. */
    content_update_proposals?: ContentUpdateProposal[];
    /** MUTATION: new features / sub-features prepared for review; nothing is created until accepted. */
    element_create_proposals?: ElementCreateProposal[];
    /** MUTATION: outcome of accepting one proposal. */
    content_update_result?: ContentUpdateResult | null;
    /** MUTATION: outcomes of a batch acceptance, one per proposal. */
    content_update_results?: ContentUpdateResult[] | null;
  }

  /** One new feature or sub-feature proposed from chat. */
  export interface ElementCreateProposal {
    proposal_key: string;
    parent_id: string;
    parent_kind?: 'module' | 'feature';
    parent_name?: string;
    kind: 'feature' | 'sub-feature';
    name: string;
    description?: string | null;
    /** Sub-features created together with the feature (read from a screen image). */
    children?: string[] | null;
    /** The screen the feature was read from; the Screen record is created on acceptance. */
    screen_name?: string | null;
  }

  /** What a proposal card shows: either description changes or a new element. */
  export type ChatProposal = ContentUpdateProposal | ElementCreateProposal;

  /** One block-level change of a description, as markdown for review. */
  export interface ContentUpdateChange {
    change: 'add' | 'replace' | 'remove';
    /** What kind of block it is: Paragraph, List, User Story, Acceptance Criteria … */
    label?: string;
    before?: string | null;
    after?: string | null;
  }

  /** Requested changes to the description of one page, module or feature in the Tree. */
  export interface ContentUpdateProposal {
    proposal_key: string;
    artifact_type: 'FEATURE' | 'PROJECT_PAGE';
    element_id: string;
    element_kind?: 'page' | 'module' | 'feature' | 'sub-feature';
    element_name?: string;
    /** Only what the element and the project's stated information support. */
    changes: ContentUpdateChange[];
    /**
     * The stored proposal that applies the changes together with `suggestions`. Absent when there
     * are no suggestions; equal to `proposal_key` when there is nothing else to apply.
     */
    suggested_key?: string | null;
    /** Points the assistant added without a source. */
    suggestions?: ContentUpdateSuggestion[];
    /** Set when the change was found, not requested: a stated decision contradicts the element's text. */
    reason?: string | null;
  }

  export interface ContentUpdateSuggestion {
    label?: string;
    markdown: string;
  }

  export type ContentUpdateStatus = 'APPLIED' | 'STALE' | 'REFUSED' | 'FAILED' | 'REJECTED';

  export interface ContentUpdateResult {
    proposal_key: string;
    status: ContentUpdateStatus;
    error_code?: string;
    /** Set when an accepted creation was applied. */
    created_element_id?: string;
  }

  // ─── Held document decision (ANALYZE analyze_document) ─────────

  export type DocumentAssessment = 'UNRELATED' | 'CONFLICTING' | 'UNCERTAIN' | 'FAILED';
  export type DocumentDecisionAction = 'ACCEPT' | 'REJECT';

  export interface DocumentDecisionConflict {
    topic: string;
    current_value?: string | null;
    current_source?: string | null;
    document_claim?: string | null;
    document_source?: string | null;
  }

  export interface DocumentDecisionOption {
    action: DocumentDecisionAction;
    label: string;
  }

  export interface DocumentDecisionEntry {
    decision_key: string;
    document_key: string;
    file_name: string;
    assessment: DocumentAssessment | string;
    reason?: string | null;
    conflicts?: DocumentDecisionConflict[] | null;
    clarification_question?: string | null;
    content_truncated?: boolean;
    options?: DocumentDecisionOption[] | null;
  }

  export interface DocumentDecisionBlock {
    decisions: DocumentDecisionEntry[];
    /** File names of other documents still waiting for a decision. */
    held_documents?: string[] | null;
  }

  /** `POST /api/agent/document-decision` body. */
  export interface DocumentDecisionRequest {
    workspaceId: string | null | undefined;
    projectId: string | null | undefined;
    decisionKey: string;
    action: DocumentDecisionAction;
    note?: string | null;
    aiProvider?: AiProvider;
  }

  export interface DocumentDecisionResponse {
    decisionKey: string;
    documentKey: string;
    fileName: string;
    status: 'ACCEPTED' | 'REJECTED' | 'AUTO_ACCEPTED' | string;
    ingestionState?: 'IN_PROGRESS' | 'DONE' | 'FAILED' | null;
    alreadyIngested?: boolean;
    message?: string | null;
    /** ACCEPT → the ingestion chat turn (rendered like any chat response). REJECT → null. */
    chat?: AgentChatApiResponse | null;
  }

  /**
   * Persisted history envelope. Deliberately tolerant: rows written before F5-T5.2 (and before
   * several prompt revisions) can carry shapes the live contract no longer accepts, and rewriting
   * history is out of scope. Live provider output NEVER uses this type.
   */
  export type LegacyAgentEnvelope = Omit<AgentEnvelope, 'chat_response' | 'project_context'> & {
    chat_response?: ChatResponseText;
    /** Tolerant: rows written before the flat-string-list prompt rule carry `{topic, ...}` objects. */
    project_context?: ProjectContext | null;
    [key: string]: unknown;
  };

  /** `questions[]` for enrich_context, `open_questions[]` for solve_open_points. */
  export function interviewQuestionsOf(env: AgentEnvelope): Question[] {
    if (env.questions?.length) return env.questions;
    return env.open_questions ?? [];
  }

  /** INTERVIEW enrich_context phase=answers emits one entry per answered Q. */
  export interface EnrichInput {
    topic: string;
    detail: string;
  }

  /**
   * Backend-internal adapted shape — store bu iç modeli tüketir. Gerçek HTTP
   * yanıtı {@link AgentChatApiResponse}; `normalizeAgentResponse()` helper'ı
   * yeni envelope + top-level alanlardan bu şekli türetir.
   *
   * `action` alanı `agent` + `variant_key`'den derive edilir: ANALYZE.analyze_document,
   * ANSWER→'answer', CLARIFY→'clarify', BUILDER→'route_to_agent', vb.
   *
   * Legacy alanlar ({@link document}, {@link free_input}, …) yeni backend
   * envelope'unda yok — {@code agent-test.view.vue} gibi legacy tüketicileri
   * için opsiyonel tutuldu; yeni akışta daima {@code undefined}.
   */
  export interface ChatAgentResponse {
    action: ChatAction | string;
    chat_response: ChatResponseText;
    /**
     * Top-level next_actions tag listesi (ör. {@code ["[NA:GENERATE|CONFIDENCE:72]"]}).
     * Backend kontratı (handoff §K2) gereği her zaman array; tag yoksa boş array
     * veya {@code null}. Adapter raw HTTP DTO'sundan buraya kopyalar.
     */
    next_actions: string[] | null;
    /**
     * Aggregated context items derived from `inputs[*].<bucket>`. The store
     * walks the LLM envelope's `inputs[]` and rolls each nested bucket up to
     * these flat lists for the Context Card.
     */
    captured_topics?: ContextItem[] | null;
    open_points?: ContextItem[] | null;
    project_notes?: ContextItem[] | null;
    decisions?: DecisionItem[] | null;
    entities?: EntityItem[] | null;

    // ANALYZE modify/remove fields — backend echoes these so the store can
    // mirror the in-place change locally without waiting for projectState sync.
    target?:          AnalyzeTarget | null;
    target_title?:    string | null;
    target_input_id?: string | null;
    new_title?:       string | null;
    new_summary?:     string | null;
    new_category?:    ProjectNoteCategory | null;
    new_rationale?:   string | null;
    new_stakeholder?: string | null;
    new_type?:        EntityType | null;

    // ── Legacy fields (only consumed by the experimental agent-test view) ──
    document?: { id: string; name: string; original_summary: string } | null;
    free_input?: { id: string; summary: string } | null;
    manual_input?: { topic: string; detail: string } | null;
    manual_input_modification?: {
      target_id: string;
      topic: string;
      old_detail: string;
      new_detail: string;
    } | null;
    pending_free_input?: { summary: string; source: string; document_name?: string } | null;
    agent?: string | null;
    handoff?: {
      to_agent: string;
      generation_type: GenerationType;
      project_summary?: string | null;
      project_context?: ProjectContext | null;
      existing_structure?: Structure | null;
    } | null;
  }

  /**
   * HTTP yanıt tipi — {@code POST /api/agent/chat} endpoint'inin döndürdüğü DTO.
   * Backend-side karşılığı: {@code io.featureplanner.dto.agent.AgentChatResponse}.
   * <p>
   * Wrapper field'lar camelCase. {@link #agentResponse} ise LLM envelope —
   * içindeki key'ler ({@code chat_response}, {@code next_actions},
   * {@code project_context}, vb.) prompt sözleşmesi gereği snake_case kalır.
   */
  export interface AgentChatApiResponse {
    /** Son agent'ın LLM envelope'u — canonical kaynak. */
    agentResponse: AgentEnvelope;
    /** Çalışan agent: INTENT_ROUTER | ANSWER | CLARIFY | QUERY | MUTATION | ANALYZE | BUILDER | INTERVIEW. */
    agent: AgentName;
    /** ANALYZE için action, BUILDER/INTERVIEW için mode. Standalone'larda null. */
    variantKey?: string | null;
    /**
     * Orchestrator'un parse ettiği NA tag listesi — NA bar için kullanılır.
     * Backend her zaman array döner; tag yoksa boş array.
     */
    nextActions?: string[] | null;
    /** Backend tarafından güncellenmiş proje durumu — frontend store'a merge eder. */
    projectState?: ProjectState | null;
    // Observability (debug amacıyla sakladığımız alanlar)
    usedPromptRows?: string[];
    agentChain?: string[];
    toolCalls?: string[];
    error?: string;
  }

  // ─── Structure Request / Response ────────────────────────────

  /**
   * Structure üretim isteği. Wrapper field'lar (aiProvider, projectId,
   * workspaceId) camelCase. LLM-touching field'lar (project_summary,
   * project_context, existing_structure, resolve_questions, enrich_topics,
   * generation_type, mode) prompt sözleşmesi gereği snake_case kalır.
   */
  export interface AgentStructureRequest {
    generation_type: GenerationType;
    mode: AgentMode;
    project_summary?: string | null;
    project_context?: ProjectContext | null;
    existing_structure?: Structure | null;
    resolve_questions?: ResolveAnswer[] | null;
    enrich_topics?: string[] | null;
    /** AI sağlayıcı: "openai" | "claude" — belirtilmezse backend "openai" kullanır */
    aiProvider?: AiProvider;
    projectId?: string | null;
    workspaceId?: string | null;
  }

  export interface ResolveAnswer {
    question_id: string;
    answer_id: string;
    answer_label: string;
  }

  // ─── Structure Types ─────────────────────────────────────────

  /**
   * Module / feature / page summaries are stored as an append-only history
   * array: every change appends a new entry, prior entries stay. Renderers
   * read `summary[last].text` via the {@link summaryText} helper.
   */
  export interface SummaryEntry {
    text: string;
    created_at?: string;
    modified_at?: string;
  }

  export interface OpenPointUpdateProposal {
    element_id: string;
    original_open_point: string | {title?: string | null; summary?: string | null};
    proposed_open_point: string;
    reason: string;
  }

  /** A stored summary contradicts a later user decision; nothing changes until it is accepted. */
  export interface SummaryUpdateProposal {
    element_id: string;
    element_kind?: 'page' | 'module' | 'feature';
    element_name?: string;
    original_summary: string;
    proposed_summary: string;
    conflicting_input: string;
    reason: string;
  }

  export interface Structure {
    /** Review-only card metadata; excluded from current structure and insert payloads. */
    open_point_update_proposals?: OpenPointUpdateProposal[];
    summary_update_proposals?: SummaryUpdateProposal[];
    proposal_source_message_id?: number;
    status: 'completed' | 'questions_ready' | 'enrich_questions_ready';
    generation_type?: GenerationType;
    confidence?: number;
    assumption_label?: string;
    project_context?: ProjectContext;
    pages?: Page[];
    sections?: Section[];
    modules?: Module[];
    global_assumptions?: string[];
    open_questions?: Question[];
    enrich_questions?: Question[];
    inserted_at?: string;
    /** When set, the card renders in diff mode (NEW / UPDATED / UNCHANGED). */
    diff?: StructureDiff;
  }

  export interface Page {
    id: string;
    type: string;
    name: string;
    summary: SummaryEntry[];
    open_points?: string[];
    /** Populated by builder-diff.helper when a Structure carries `diff`. */
    _diffStatus?: DiffStatus;
    _changes?: string;        // e.g. "Renamed from X", "Summary refined"
    _prevSummary?: string;    // previous-turn summary when status=updated
  }

  export interface Section {
    id: string;
    name: string;
    modules: Module[];
  }

  export interface Module {
    id: string;
    name: string;
    summary: SummaryEntry[];
    color: string;
    open_points?: string[];
    features?: Feature[];
    /** Populated by builder-diff.helper when a Structure carries `diff`. */
    _diffStatus?: DiffStatus;
    _changes?: string;
    _prevSummary?: string;
    /** Counts after applyDiffPayload merge — used by the card to show badges. */
    _diffDelta?: { added_count: number; resolved_count: number; resolved_texts?: string[] };
  }

  export interface Feature {
    id: string;
    name: string;
    summary?: SummaryEntry[];
    subfeatures?: Subfeature[];
    open_points?: string[];
    /** Populated per-feature inside a diff-annotated module. */
    _diffStatus?: DiffStatus;
    _changes?: string;
    _prevSummary?: string;
    /** Counts after applyDiffPayload merge — used for badges. */
    _diffDelta?: { added_count: number; resolved_count: number; resolved_texts?: string[] };
  }


  // ─── Structure diff (regenerate / builder mode=diff) ──────────

  export type DiffStatus = 'new' | 'updated' | null;

  /** Single entry in a diff bucket — references an item by id (and optional type). */
  export interface DiffEntry {
    id: string;
    name?: string;
    type?: 'page' | 'module';
  }

  /**
   * Optional payload on Structure when the backend returns a diff against
   * the previous committed structure (BUILDER_MODE_DIFF). When missing, the
   * client computes diff buckets locally from `currentStructure`.
   */
  export interface StructureDiff {
    new?: DiffEntry[];
    updated?: DiffEntry[];
    unchanged?: DiffEntry[];
  }

  // ─── BUILDER_MODE_DIFF delta payload (new backend shape) ──────
  //
  // When backend runs in `diff` mode it returns this delta payload instead
  // of a full Structure. The client applies it against the current
  // committed structure via applyDiffPayload() in builder-diff.helper.
  //
  // `new_modules[]` — full module objects (no prior state → all open_points "added").
  // `updated_modules[]` — delta per existing module (only changes; unchanged is omitted).
  // Open points in updated entries use the delta shape:
  //   - added_open_points[]     — new open-point texts from the new input
  //   - resolved_open_points[]  — VERBATIM existing open-point texts that got resolved
  // Client removes resolved_open_points from existing list via text-match (module-scoped).

  export interface DiffPayload {
    status: 'diff_completed';
    new_modules: NewModuleDelta[];
    updated_modules: UpdatedModuleDelta[];
  }

  export interface NewModuleDelta {
    id: string;
    /** Frontend-only grouping hint; no BUILDER prompt emits it, so it is usually absent. */
    section_id?: string | null;
    name: string;
    summary: SummaryEntry[];
    open_points: string[];
    features?: NewFeatureDelta[];
  }

  export interface NewFeatureDelta {
    id: string;
    name: string;
    summary: SummaryEntry[];
    open_points: string[];
  }

  export interface UpdatedModuleDelta {
    id: string;
    name?: string;
    summary?: SummaryEntry[];
    added_open_points: string[];
    resolved_open_points: string[];
    /**
     * BUILDER_MODE_DIFF.md allows a brand new, fully shaped feature inside an updated module. The
     * two shapes are told apart by the delta slots: an update carries them, a creation never does
     * and carries `open_points` instead.
     */
    features?: FeatureDelta[];
  }

  export type FeatureDelta = UpdatedFeatureDelta | NewFeatureDelta;

  export interface UpdatedFeatureDelta {
    id: string;
    name?: string;
    summary?: SummaryEntry[];
    added_open_points: string[];
    resolved_open_points: string[];
  }

  export interface Subfeature {
    id: string;
    name: string;
    summary?: string;
  }

  export interface Question {
    id: string;
    text: string;
    type: 'single_select' | 'multi_select';
    question_index?: number;
    total_questions?: number;
    element_id?: string | null;
    element_name?: string | null;
    /**
     * Open-point text the question clarifies. The INTERVIEW prompt
     * (INTERVIEW_MODE_SOLVE_OPEN_POINTS.md) emits this as `open_point`.
     * `assumption` is a legacy alias from earlier prompt versions and is
     * still accepted on the wire for backwards compatibility.
     */
    open_point?: string | null;
    /** @deprecated Use {@link open_point}. */
    assumption?: string | null;
    /**
     * Backend-stamped canonical `ProjectOpenPoint` identity (F2-T2.3). Present only when
     * `PROJECT_KNOWLEDGE` is enabled and the backend matched this question's element_id +
     * open_point text to a canonical row; the LLM never produces this value itself. Echo it
     * back verbatim in {@link ResolveQuestionAnswer.open_point_key} so the backend can resolve
     * the correct canonical record instead of relying on free-text matching alone. Absent for
     * flag-off turns or unmatched entries — the existing free-text resolve flow is unaffected.
     */
    open_point_key?: string | null;
    options: { id: string; label: string; command: string }[];
  }

  // ─── Chat UI Types ────────────────────────────────────────────

  export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    hint?: string | null;
    /** Backend'in mesajla ilişkili NA tag listesi — UI bu mesaj için NA bar kararını verir. */
    nextActions?: string[] | null;
    action?: ChatAction | string | null;
    timestamp: number;
    /**
     * Attachment chips rendered above a USER bubble.
     * - From history: hydrated from {@link ChatUserEnvelope.documents}, both fields populated.
     * - From a live send: {@code documentKey} is null until the next bootstrap/refresh
     *   pulls the persisted envelope. The chip stays unclickable until then.
     */
    documents?: ChatMessageDocument[] | null;
  }

  export interface ChatMessageDocument {
    documentKey: string | null;
    fileName: string;
    /** T6.3: a screen image attached to the turn — the chip shows a thumbnail from this URL. */
    imageUrl?: string | null;
  }

  // ─── Context Card items ───────────────────────────────────────
  // Topics / open points / project notes share the same shape: a title +
  // optional summary, plus metadata for grouping and diff tagging.

  export interface ContextItem {
    title: string;
    summary?: string;
    /** UI-Bucket — bestimmt unter welchem Tab das Item im Context Card landet. */
    bucket?: ContextBucket;
    /** Id of the input[] entry that owns this item (for in-place mutations). */
    parentInputId?: string;
    /** project_notes use a closed category enum. */
    category?: ProjectNoteCategory;
    // Diff tag: 'NEW' on the item added in the last LLM call; 'UPDATED' when
    // its summary changed. Cleared after one user turn so tags always reflect
    // the most recent change.
    status?: 'NEW' | 'UPDATED' | null;
  }

  /**
   * Aggregated decision row — flattens `inputs[*].decisions[*]` + provenance.
   * `title` is the matching key for modify/remove; the other fields enrich
   * the Context Card detail view.
   */
  export interface DecisionItem {
    title: string;
    quote: string | null;
    stakeholder: string | null;
    rationale: string;
    bucket?: ContextBucket;
    parentInputId?: string;
    status?: 'NEW' | 'UPDATED' | null;
  }

  /**
   * Aggregated entity row — flattens `inputs[*].entities[*]` + provenance.
   * Uses `name` as the matching key.
   */
  export interface EntityItem {
    name: string;
    type: EntityType;
    summary: string;
    bucket?: ContextBucket;
    parentInputId?: string;
    status?: 'NEW' | 'UPDATED' | null;
  }

  export interface NextActionButton {
    label: string;
    subtext?: string;
    command: string;
    type: 'primary' | 'secondary' | 'accent';
  }

  // ─── Next-action groups (POC-parity rendering) ───────────────

  export interface NaButton {
    id: string;
    name: string;
    subtext?: string;
    command: string;
    disabled?: boolean;
    /**
     * Optional inline-Badge neben dem Slug (z.B. "(1)"). Rein visuell —
     * beeinflusst weder slugify noch das command-dispatching. Use-Case:
     * Quantifier wie "Show Analysis (1)" für DOC_CONFLICT NA.
     */
    countBadge?: string;
    /**
     * Marks this button as opening a sub-menu — renderer appends a chevron
     * (`›`) after the slug to signal "more options inside". Currently used
     * for `/discovery` which expands into ENRICH_DEPTH (Quick/Medium/Deep).
     */
    submenu?: boolean;
    /**
     * Marks this button as a "go back to parent menu" navigation — renderer
     * prepends a chevron (`‹`) before the slug to differentiate it from
     * action buttons. Pairs with `submenu: true` siblings (the parent menu's
     * button that opened this submenu).
     */
    back?: boolean;
  }

  export interface NaGroup {
    id?: string;
    title?: string;
    buttons?: NaButton[];
    separator?: boolean;
    contextRow?: boolean;
  }

  export interface NaDefinition {
    groups: NaGroup[];
  }

  // ─── Tool Test ────────────────────────────────────────────────

  export interface ToolTestRequest {
    message: string;
    aiProvider?: AiProvider;
  }

  export interface ToolTestResponse {
    responseText?: string;
    toolCalls?: string[];
    provider?: string;
    model?: string;
    error?: string;
  }

  // ─── Chat History (bootstrap + messages) ──────────────────────
  // Backend karşılıkları: io.featureplanner.dto.agent.Chat*Request /
  // ChatBootstrapResponse / ChatMessageDto / ChatMessagesPage.
  //
  // Sade mimari: bir mesaj artık parsed alanlar (content, hint, builderCard,
  // contextCard, action, agent, variantKey, nextActions) tutmaz; tek bir
  // {@link ChatMessageDto.envelope} alanında orchestrator'ın frontend'e
  // gönderdiği payload aynen saklanır.
  //
  //   • USER mesajı için envelope:
  //       { user_input: string|null,
  //         file_name: string|null,
  //         file_storage_ref?: string|null }
  //
  //   • ASSISTANT mesajı için envelope:
  //       { agent_response: AgentEnvelope,    // LLM envelope (chat_response, ...)
  //         agent: string,                    // INTENT_ROUTER | ANSWER | ...
  //         variant_key: string|null,
  //         next_actions: string[],
  //         qa_snapshot?: Record<string, unknown> }
  //
  // Frontend her mesajı, store içindeki `chat()` action response'u gibi işler.

  export type AgentChatMessageRole = 'USER' | 'ASSISTANT' | 'SYSTEM';

  /** {@code POST /api/agent/chat/bootstrap} body. */
  export interface ChatBootstrapRequest {
    workspaceId: string;
    projectId: string;
  }

  /** {@code POST /api/agent/chat/reset} body — kullanıcının kendi chat history'sini siler. */
  export interface ChatResetRequest {
    workspaceId: string;
    projectId: string;
  }

  /** {@code POST /api/agent/chat/reset} response — silinen mesaj sayısı. */
  export interface ChatResetResponse {
    deleted: number;
  }

  /** {@code POST /api/agent/chat/messages} body — cursor-based pagination. */
  export interface ChatListMessagesRequest {
    workspaceId: string;
    projectId: string;
    /**
     * Cursor: bu createDate (ISO Instant) değerinden eski mesajlar döner. İlk
     * sayfa için null.
     */
    before?: string | null;
    /** Sayfa boyutu — null veya 0 → backend default 30, max 200. */
    limit?: number | null;
  }

  /**
   * Backend persisted USER envelope shape.
   * <p>
   * `documents[]` is the canonical attachment list (since the multi-doc upload
   * release). `file_name` is a legacy fallback kept for old single-file
   * messages — render `documents[]` when present and ignore `file_name`.
   */
  export interface ChatUserEnvelope {
    user_input?: string | null;
    documents?: ChatEnvelopeDocument[] | null;
    /** T6.3: screen images stored for this turn. snake_case. */
    images?: ChatEnvelopeImage[] | null;
    file_name?: string | null;
    file_storage_ref?: string | null;
  }

  export interface ChatEnvelopeImage {
    image_key: string;
    file_name?: string | null;
    url?: string | null;
  }

  /** Document reference inside a persisted USER envelope. snake_case. */
  export interface ChatEnvelopeDocument {
    document_key: string;
    file_name: string;
  }

  /**
   * ASSISTANT mesajı için envelope shape'i. {@code agent_response} LLM'in tam
   * envelope'u — {@link AgentEnvelope} ile aynı (chat_response, status,
   * next_actions, +flat slots).
   * <p>
   * Per-turn snapshot'lar — orchestrator persist sırasında üretip envelope
   * JSONB içine gömer:
   * <ul>
   *   <li>{@link builder_card} — BUILDER mesajının structure-shape alt-kümesi
   *       (sections/pages/modules/new_modules/updated_modules + project_context
   *       + generation_type/confidence/diff). Frontend hidrasyonda doğrudan
   *       {@code builderCards[msgId]}'a koyar.</li>
   *   <li>{@link context_card} — ANALYZE context-action mesajının O TURN'DEKİ
   *       proje state snapshot'ı: {@code { docNames, capturedTopics,
   *       openPoints, projectNotes }}. Geçmiş scroll'da o anki tabloyu
   *       koruyabilmek için zorunlu.</li>
   * </ul>
   */
  export interface ChatAssistantEnvelope {
    agent_response: LegacyAgentEnvelope;
    agent: string;
    variant_key?: string | null;
    next_actions: string[];
    builder_card?: Record<string, unknown> | null;
    context_card?: {
      docNames?: string[];
      capturedTopics?: ContextItem[];
      openPoints?: ContextItem[];
      projectNotes?: ContextItem[];
      decisions?: DecisionItem[];
      entities?: EntityItem[];
    } | null;
    qa_snapshot?: Record<string, unknown> | null;
  }

  /** Bir mesaj — backend persist katmanından dönen sade form. */
  export interface ChatMessageDto {
    id: number;
    /** ISO Instant — sıralama burası üzerinden (ASC). */
    createDate: string;
    role: AgentChatMessageRole;
    /**
     * Role'e göre {@link ChatUserEnvelope} ya da {@link ChatAssistantEnvelope}.
     * Frontend role'üne göre cast edip render eder.
     */
    envelope: ChatUserEnvelope | ChatAssistantEnvelope | Record<string, unknown> | null;

    // ---- Audit (frontend tüketmez; debug) ----
    agentChain?: string[] | null;
    usedPromptRows?: string[] | null;
    toolCalls?: string[] | null;
  }

  /** Cursor-based mesaj sayfası response'u. */
  export interface ChatMessagesPage {
    /** Bu sayfadaki mesajlar — her zaman createDate ASC sırada. */
    messages: ChatMessageDto[];
    /** Bu sayfadan eski mesaj var mı? */
    hasMore: boolean;
    /**
     * Bir sonraki sayfa cursor'ı — bu sayfanın en eski mesajının createDate'i
     * (ISO Instant). Sayfa boşsa null.
     */
    before: string | null;
  }

  /** Bootstrap response — chat ekranı açılışında tek call'da alınan tüm restore verisi. */
  export interface ChatBootstrapResponse {
    /** Proje seviyesi paylaşımlı state. LLM-touching, içeride snake_case. */
    projectState: ProjectState;
    /** Bu kullanıcının bu projedeki son N mesajı — createDate ASC. */
    messages: ChatMessageDto[];
    hasMoreMessages: boolean;
    /**
     * En eski yüklenen mesajın createDate'i (ISO Instant) — sonraki sayfa
     * cursor'ı. Boş history'de null.
     */
    messagesBefore: string | null;
    /** Yarım kalmış INTERVIEW state — varsa frontend QaWidget'i restore eder. */
    qaSnapshot?: Record<string, unknown> | null;
  }

  // ─── Routes (extension) ──────────────────────────────────────
  // AgentRouteEnums genişletildi — LlmAuditList eklendi.
  // Not: enum yukarıda tanımlı; aşağıdaki sabit yalnızca route path eşlemesi için.

  /** {@code POST /api/agent/llm-audit/list} body. */
  export interface LlmAuditListRequest {
    workspaceId: string;
    projectId: string;
    /** Cursor — bu createDate (ISO Instant) değerinden eski kayıtlar döner. */
    before?: string | null;
    /** Sayfa boyutu — default 50, max 200. */
    limit?: number | null;
    /** Opsiyonel: yalnızca bu user'ın audit kayıtları. */
    targetUserId?: number | null;
    /**
     * Chain mode — set edildiğinde yalnızca bu asistan mesajının zincirindeki
     * audit kayıtlarını sequenceInTurn ASC sırada döndürür. Pagination yapılmaz.
     */
    agentChatMessageId?: number | null;
  }

  /** {@code POST /api/agent/llm-audit/detail} body. */
  export interface LlmAuditDetailRequest {
    workspaceId: string;
    projectId: string;
    id: number;
  }

  /**
   * Audit list satırı — büyük text alanlar yok, yalnızca uzunlukları + meta.
   * Detay için ayrı endpoint çağrılır.
   */
  export interface LlmAuditSummary {
    id: number;
    createDate: string;
    sequenceInTurn: number;
    agentChatMessageId: number | null;
    userId: number;

    agent: string | null;
    variantKey: string | null;
    provider: string | null;
    model: string | null;

    systemInstChars: number;
    userMessageChars: number;
    responseTextChars: number;
    recentTurnsCount: number;
    toolCount: number;
    jsonMode: boolean | null;

    promptTokens: number | null;
    completionTokens: number | null;
    totalTokens: number | null;
    cacheReadTokens: number | null;
    cacheWriteTokens: number | null;

    durationMs: number | null;
    hasError: boolean;
  }

  /** Audit detail — tüm raw alanlar. */
  export interface LlmAuditDetail {
    id: number;
    createDate: string;
    sequenceInTurn: number;
    agentChatMessageId: number | null;
    userId: number;

    agent: string | null;
    variantKey: string | null;
    provider: string | null;
    model: string | null;

    systemInst: string | null;
    userMessage: string | null;
    recentTurns: { role: string; text: string }[];
    toolNames: string[];
    jsonMode: boolean | null;

    responseText: string | null;
    responseParsed: Record<string, unknown> | null;
    error: string | null;

    promptTokens: number | null;
    completionTokens: number | null;
    totalTokens: number | null;
    cacheReadTokens: number | null;
    cacheWriteTokens: number | null;

    durationMs: number | null;
  }

  /** Cursor-based audit list response. */
  export interface LlmAuditPage {
    /** DESC sırada (yeni → eski). */
    rows: LlmAuditSummary[];
    hasMore: boolean;
    /** Sonraki sayfa cursor'ı — bu sayfanın en eski kaydının createDate'i. */
    before: string | null;
  }

  // ─── Agent Test Harness ──────────────────────────────────────

  export type TestMode = 'routing_only' | 'full_chain'
  export type RunStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED'
  export type ScenarioStatus = 'PASS' | 'FAIL' | 'SKIP' | 'ERROR'

  export interface TestRunRequest {
    workspaceId: string
    scenarioIds?: string[]
    tags?: string[]
    modeOverride?: TestMode
    iterationsOverride?: number
    providerOverride?: string
  }

  export interface TestRunStartResponse {
    runId: string
    scenarioCount: number
    status: string
  }

  export interface TestRunSummary {
    total: number
    passed: number
    failed: number
    flaky: number
    skipped: number
    errors: number
  }

  export interface TestIteration {
    auditTraceId?: string
    auditIds?: number[]
    actual?: Record<string, unknown>
    expected?: Record<string, unknown>
    hardPass: boolean
    schemaPass: boolean
    softWarnings: string[]
    durationMs: number
    errorMessage?: string
  }

  export interface TestScenarioResult {
    id: string
    description: string
    mode: TestMode
    status: ScenarioStatus
    passRate: string
    iterations: TestIteration[]
    skipReason?: string
  }

  export interface TestRunReport {
    runId: string
    status: RunStatus
    startedAt?: string
    completedAt?: string
    summary?: TestRunSummary
    scenarios?: TestScenarioResult[]
    errorMessage?: string
  }

  export interface TestScenarioInfo {
    id: string
    mode: TestMode
    description: string
    tags: string[]
    iterations: number
    threshold: number
    skip: boolean
    expectedIntent: string
    expectedVariantKey?: string
  }
}

/**
 * Extract the latest visible text from a SummaryEntry history array.
 * Centralized so renderers don't duplicate the `array[last]` access pattern.
 */
export function summaryText(
  item: { summary?: AgentNs.SummaryEntry[] } | null | undefined,
): string {
  const arr = item?.summary;
  if (!Array.isArray(arr) || arr.length === 0) return '';
  return arr[arr.length - 1]?.text ?? '';
}
